import stringify from 'json-stable-stringify';
import omit from 'lodash/omit';

import {type RunsDataQueryConfig} from '../../../../containers/RunsDataLoader';
import {type BucketedHistorySpec} from '../../../../state/runs/types';
import {type SingleQuery} from '../../../../util/queryTypes';
import {type BucketedQueryType, queryVars} from './queryVars';

/** The fields of a request that bucketedQuery turns into a fetch. */
export interface KeyedRequest {
  expressions: string[] | undefined;
  nBuckets: number;
  queryType: BucketedQueryType;
  runsDataQuery: RunsDataQueryConfig;
  singleQuery: SingleQuery;
}

/**
 * Stable request keys built from query variables. Equal `exact` keys share
 * an entry; the other keys allow specific differences for cached-data reuse.
 */
export interface RequestKeys {
  exact: string;
  /** Ignores the bucket count: the same data at another resolution. */
  fidelity: string;
  /** Ignores the zoom bounds: the same data over another range. */
  family: string;
  /** Ignores the zoom bounds and which runs are selected. */
  selection: string;
}

const GROUPED_QUERY_TYPES = new Set<BucketedQueryType>([
  'GroupedBucketedQuery',
  'GroupedBucketedQueryId',
  'GroupedBucketedBackendLinesQuery',
]);

/** Grouped queries return one line per group instead of one per run. */
export const isGroupedQueryType = (queryType: BucketedQueryType) =>
  GROUPED_QUERY_TYPES.has(queryType);

const ZOOM_BOUNDS = ['minStep', 'maxStep', 'minX', 'maxX'] as const;

export function requestKeys({
  expressions,
  nBuckets,
  queryType,
  runsDataQuery,
  singleQuery,
}: KeyedRequest): RequestKeys {
  const vars = queryVars(
    runsDataQuery,
    singleQuery,
    nBuckets,
    queryType,
    expressions,
    'Run History Line Plot'
  );
  const base = {
    ...omit(vars, ['bucketedHistorySpecs', 'expressions']),
    // The server reads expressions only to decide whether to aggregate groups
    // (shouldAggregate in gorilla's resolver/project.go), and only the grouped
    // documents send them.
    hasExpressions: isGroupedQueryType(queryType)
      ? (expressions?.length ?? 0) > 0
      : undefined,
    // Picks the GraphQL document; queryVars does not include it.
    queryType,
    // Never sent, but stamped on every returned line, so two runsets must
    // never share data.
    runset: singleQuery.id,
  };
  // queryVars sends each spec as a JSON string; parse it so field order in
  // the panel's spec cannot change the key.
  const specs = vars.bucketedHistorySpecs.map(
    spec => JSON.parse(spec) as BucketedHistorySpec
  );
  const unbounded = specs.map(spec => omit(spec, ZOOM_BOUNDS));
  return {
    exact: stringify({...base, specs}),
    fidelity: stringify({
      // queryVars includes the bucket count both here and inside each spec.
      ...omit(base, 'bins'),
      specs: specs.map(spec => omit(spec, 'bins')),
    }),
    family: stringify({...base, specs: unbounded}),
    selection: stringify({
      ...omit(base, ['filters', 'selections']),
      specs: unbounded,
    }),
  };
}
